// src/ui/Layout.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import '../css/Ui.css';
import axios from 'axios';

// ✅ 모달 컴포넌트 import
import ApprovalFormSelectModal from '../components/Modal/ApprovalFormSelectModal';
import ApprovalOpinionModal from '../components/Modal/ApprovalOpinionModal';

function Layout({ children }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // ✅ 사용자 검색 관련 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // ✅ 결재양식 모달 상태
  const [showFormModal, setShowFormModal] = useState(false);
  const [formsTree, setFormsTree] = useState([]);

  // ✅ 결재 의견 모달 상태
  const [showOpinionModal, setShowOpinionModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingCallback, setPendingCallback] = useState(null);

  // 📑 결재양식 트리 조회
  useEffect(() => {
    const fetchFormsTree = async () => {
      try {
        const token = sessionStorage.getItem('token');
        if (!token) return;
        const res = await axios.get('/api/forms?view=tree', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFormsTree(res.data);
      } catch (err) {
        console.error('결재양식 트리 불러오기 실패:', err);
      }
    };
    fetchFormsTree();
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token || location.pathname === '/login') {
      return;
    }

    const cacheKey = 'tdx:last-page-access-log';
    const now = Date.now();

    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached?.path === location.pathname && now - Number(cached.time || 0) < 1500) {
        return;
      }
    } catch {
      // ignore cache parse failures
    }

    sessionStorage.setItem(cacheKey, JSON.stringify({ path: location.pathname, time: now }));

    axios.post(
      '/api/logs/page-access',
      { path: location.pathname },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch((err) => {
      console.error('페이지 접근 로그 저장 실패:', err);
    });
  }, [location.pathname]);

  const handleOpenFormModal = () => setShowFormModal(true);
  const handleCloseFormModal = () => setShowFormModal(false);

  const handleSelectForm = (formCode) => {
    setShowFormModal(false);
    window.location.href = `/aprvdoc/form?form_code=${formCode}`;
  };

  // ✅ 결재 의견 모달 열기 핸들러 (AprvDocView에서 호출)
  const openOpinionModal = (action, callback) => {
    setPendingAction(action);
    setPendingCallback(() => callback);
    setShowOpinionModal(true);
  };


  // 라우트 컴포넌트 이름 비교에 의존하지 않고 공통 핸들러를 항상 주입
  const injectedChild = React.cloneElement(children, {
    collapsed: false,
    onOpenFormModal: handleOpenFormModal,
    onOpenOpinionModal: openOpinionModal,
  });

  // 📑 사용자 검색
  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (term.trim()) {
      try {
        const token = sessionStorage.getItem('token');
        const res = await axios.get(`/api/auth/users?search=${encodeURIComponent(term)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setResults(res.data);
        setShowPopup(true);
        setSelectedUser(null);
      } catch (err) {
        console.error('검색 실패:', err);
      }
    }
  };

  return (
    <div className="d-flex">
      {/* 좌측 Sidebar */}
      <Sidebar collapsed={collapsed} onOpenFormModal={handleOpenFormModal} />

      {/* Navbar + Content */}
      <div className="main-wrapper flex-grow-1 d-flex flex-column">
        <Navbar onToggle={() => setCollapsed(!collapsed)} onSearch={handleSearch} />
        <main className="content">{injectedChild}</main>

        {/* ✅ 검색 결과 팝업 */}
        {showPopup && (
          <div className="search-popup-overlay">
            <div className="search-popup card shadow">
              <div className="card-header d-flex justify-content-between">
                <h5>🔍 "{searchTerm}" 검색 결과</h5>
                <button className="btn-close" onClick={() => setShowPopup(false)}></button>
              </div>

              <div className="card-body">
                {results.length === 0 ? (
                  <p className="text-muted">검색 결과가 없습니다.</p>
                ) : (
                  <div className="row" style={{ minHeight: '280px' }}>
                    {/* 좌측 목록 */}
                    <div className="col-md-5 border-end">
                      <ul className="list-group list-group-flush">
                        {results.map(user => (
                          <li
                            key={user.userid}
                            className={`list-group-item d-flex align-items-center ${selectedUser?.userid === user.userid ? 'active' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedUser(user)}
                          >
                            <span className="me-2">👤</span>
                            <div>
                              <div className="fw-bold">{user.name || '-'} / {user.position || '-'}</div>
                              <small className="text-muted">{user.dept_name}</small>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* 우측 상세 */}
                    <div className="col-md-7">
                      {selectedUser ? (
                        <div className="card border-0">
                          <div className="card-body">
                            <div className="d-flex align-items-center mb-3">
                              <img
                                src={
                                  selectedUser.photo_path && selectedUser.photo_path.trim() !== ""
                                    ? `${selectedUser.photo_path}?${new Date().getTime()}`
                                    : "/images/img_org_noimage.png"                   
                                }
                                alt="사용자 사진"
                                className="img-thumbnail rounded-circle me-3"
                                style={{ width: "80px", height: "80px", objectFit: "cover" }}
                              />
                              <h5 className="card-title mb-0">👤 {selectedUser.name}</h5>
                            </div>

                            <p><strong>아이디:</strong> {selectedUser.userid}</p>
                            <p><strong>부서:</strong> {selectedUser.dept_name || '-'}</p>
                            <p><strong>직책:</strong> {selectedUser.position || '-'}</p>
                            <p><strong>연락처:</strong> {selectedUser.phone || '-'}</p>
                            <p><strong>이메일:</strong> {selectedUser.email || '-'}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                          👈 좌측에서 사용자를 선택하세요
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ✅ 결재양식 선택 모달 */}
        <ApprovalFormSelectModal
          show={showFormModal}
          onClose={handleCloseFormModal}
          formsTree={formsTree}
          onSelectForm={handleSelectForm}
        />

        {/* ✅ 결재 의견 모달 */}
        <ApprovalOpinionModal
          show={showOpinionModal}
          onClose={() => setShowOpinionModal(false)}
          onConfirm={(opinion) => {
            if (pendingCallback) {
              pendingCallback(pendingAction, opinion); // AprvDocView의 handleApprovalAction 호출
            }
            setShowOpinionModal(false);
            setPendingAction(null);
            setPendingCallback(null);
          }}
          actionLabel={
            pendingAction === "approve" ? "승인" :
            pendingAction === "reject" ? "반려" :
            pendingAction === "agree" ? "찬성" :
            pendingAction === "disagree" ? "반대" : "확인"
          }
        />
      </div>
    </div>
  );
}

export default Layout;